import { isString, snakeCase } from 'lodash';
import React, { FC, ReactNode, useCallback, useEffect, useState } from 'react';
import { FieldOptionBase } from 'api/tasks';
import { SelectMode } from 'components/atoms/select';
import { getControlledSelectBasedEditor } from 'components/organisms/field-editors/components/controlled-select-based-editor';
import { TriggerContent } from 'components/organisms/field-editors/components/controlled-select-based-editor-trigger-content';
import { TranslationKey, useTranslation } from 'hooks/use-translation';
import { byLabel } from 'utils/comparators/by-label';
import { getOptionsWithValueStr } from 'utils/get-options-with-valuestr';
import { getValueStrOrValueAsNumber } from 'utils/get-value-valuestr';
import {
  MultiSelectField,
  MultiUserField,
  SingleSelectField,
  SingleUserField,
} from '../fields';
import { SelectFilterOperationType } from '../filters/select';
import { EditorComponents } from './editor-components';

interface Props {
  field:
    | SingleSelectField
    | MultiSelectField
    | SingleUserField
    | MultiUserField;
  filterType:
    | SelectFilterOperationType.IsNoneOf
    | SelectFilterOperationType.IsNot
    | SelectFilterOperationType.Is
    | SelectFilterOperationType.IsOneOf;
  options: FieldOptionBase<number>[];
  showDoneButton?: boolean;
  value: number[];
  components: EditorComponents;
  onChange: (next: number[]) => void;
  type: SelectMode;
  placeholder?: ReactNode;
}

const Editor = getControlledSelectBasedEditor<true, number>(
  'multi-select-editor',
  'editors.multi-select.select',
);

export const MultiSelectEditor: FC<Props> = ({
  field,
  value,
  filterType,
  options,
  showDoneButton,
  components: { TriggerOption, ...selectComponents },
  onChange,
  type,
  placeholder,
}) => {
  const title = useTranslation('task_list.filters.messages.editor_title', {
    field: field.fieldLabel,
    operation: useTranslation(
      `task_list.filters.${snakeCase(filterType)}` as TranslationKey,
    ),
  });

  const [latest, setLatest] = useState(value);

  const onClose = useCallback(() => {
    onChange(latest);
  }, [latest, onChange]);

  useEffect(() => {
    setLatest(value);
  }, [value, setLatest]);

  // Hacky fix, cast value arr to have all strings if one of the value is a string, we will receive value as numbers usually.
  // Edge case fix for current phases and other fields which have string values.
  // without the fix value will be [1, '2'] instead of ['1', '2']
  const valueHasString = value.some((v) => isString(v));

  const modifiedValue = valueHasString ? value.map(String) : value;

  const items = (modifiedValue ?? [])
    .map((x) => options.find((o) => getValueStrOrValueAsNumber(o) === x)!)
    .filter(Boolean)
    .sort(byLabel)
    .map((x) => x as FieldOptionBase<number>)
    .map((x) => <TriggerOption option={x} />);

  const optionsWithValueStr = getOptionsWithValueStr(options);

  return (
    <Editor
      showDoneButton={showDoneButton}
      title={title}
      components={selectComponents}
      isMulti
      options={optionsWithValueStr}
      value={latest
        .map((v) => optionsWithValueStr.find((o) => o.value === v))
        .filter((x) => x != null)
        .map((x) => x!)
        .sort(byLabel)}
      onChange={(latest_options?: FieldOptionBase<number>[]) =>
        setLatest((latest_options ?? []).sort(byLabel).map((x) => x.value))
      }
      triggerContent={
        <TriggerContent items={items} placeholder={placeholder} />
      }
      onClose={onClose}
      type={type}
    />
  );
};
